import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Layers, Heart, Handshake, Swords, Folder } from "lucide-react";
import {
  listCollections,
  createCollection,
  deleteCollection,
} from "../lib/api";

const ICONS = { main: Layers, wishlist: Heart, trade: Handshake, deck: Swords, custom: Folder };
const TYPES = [
  { value: "custom", label: "Custom" },
  { value: "trade", label: "Trade binder" },
  { value: "deck", label: "Deck" },
];
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);

export default function Collections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("custom");

  const load = async () => {
    try {
      const d = await listCollections();
      setCollections(d.collections || []);
    } catch {
      toast.error("Couldn't load collections.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await createCollection({ name: name.trim(), type });
      setName("");
      setCreating(false);
      toast.success("Collection created.");
      load();
    } catch {
      toast.error("Create failed.");
    }
  };

  const remove = async (c) => {
    if (c.type === "main" || c.type === "wishlist") {
      toast.error("Default collections can't be deleted.");
      return;
    }
    if (!window.confirm(`Delete "${c.name}" and all its cards?`)) return;
    await deleteCollection(c.id);
    toast.success("Deleted.");
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 sm:py-10">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-5xl font-light tracking-tighter text-white">Collections</h1>
          <p className="text-sm text-white/50 mt-1">Organize what you own, want, and trade.</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="new-collection-btn"
          className="btn-primary flex items-center gap-1.5 text-sm"
        >
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      {creating && (
        <div className="glass rounded-2xl p-4 mb-6 space-y-3" data-testid="new-collection-form">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Collection name"
            data-testid="new-collection-name"
            className="w-full px-3 py-2 rounded-xl bg-black/60 border border-white/15 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300/60"
          />
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => setType(t.value)}
                className={`px-3 py-1.5 rounded-full text-xs tracking-wider transition ${
                  type === t.value
                    ? "bg-amber-400/15 text-amber-200 border border-amber-300/40"
                    : "bg-white/5 text-white/60 border border-transparent hover:bg-white/10"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={create} className="btn-primary text-xs" data-testid="new-collection-save">
              Create
            </button>
            <button onClick={() => setCreating(false)} className="btn-ghost text-xs">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-24 bg-white/5 rounded-2xl" />
          <div className="h-24 bg-white/5 rounded-2xl" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((c) => {
            const Icon = ICONS[c.type] || Folder;
            return (
              <div key={c.id} className="relative group" data-testid={`collection-tile-${c.type}`}>
                <Link to={`/collections/${c.id}`} className="block">
                  <div className="glass rounded-2xl p-5 hover:border-amber-300/40 hover:bg-amber-400/[0.04] transition-colors border border-transparent">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4 text-amber-300/80" />
                      <span className="text-[10px] tracking-[0.18em] text-amber-200/60 uppercase">{c.type}</span>
                    </div>
                    <div className="text-lg text-white mb-1">{c.name}</div>
                    <div className="text-xs text-white/55">{c.card_count || 0} cards · {money(c.total_value)}</div>
                    {c.top_thumbs?.length > 0 && (
                      <div className="mt-3 flex gap-1.5">
                        {c.top_thumbs.slice(0, 3).map((t, i) => (
                          <img key={i} src={t} alt="" className="w-8 h-11 rounded object-cover border border-white/10" />
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
                {c.type !== "main" && c.type !== "wishlist" && (
                  <button
                    onClick={() => remove(c)}
                    data-testid={`delete-collection-${c.id}`}
                    aria-label="Delete"
                    className="absolute top-3 right-3 p-1.5 rounded-full bg-black/40 text-white/40 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
